import React, {forwardRef, useImperativeHandle, useRef, useState} from "react";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import Button from "../../../../../components/button/button.jsx";
import Loader from "../../../../../components/template/loader.jsx";
import {FaPlus, FaTrash} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../../utils/project-utils.js";
import DispatchService from "../../../../../services/dispatch/dispatch-service.js";
import InputFile from "../../../../../components/input/input-file.jsx";

const mapFuelTypeOptions = (list = []) =>
    list.map(item => (typeof item === "string"
        ? {value: item, label: item}
        : {value: item.value ?? item.key ?? item.id, label: item.label ?? item.name ?? item.value}));

const DispatchRefuelling = forwardRef(({dispatchId, finalMileage, garrisonId, isSuggestKm = true, place, showLocation = false}, ref) => {
    const [rows, setRows] = useState([]);
    const tempKey = useRef(0);
    const nextTempId = () => `ref-${tempKey.current++}`;

    const {data, isLoading} = useQuery({
        queryKey: ["dispatch-refuelling", dispatchId ?? "", garrisonId ?? ""],
        enabled: !!garrisonId,
        queryFn: async () => {
            const result = await DispatchService.getClosedDispatchRefuelling(dispatchId, garrisonId);
            if (result === "BAD_REQUEST") {
                projectUtils.swalErrorMessage("Error", "Não foi possível carregar dados de abastecimento");
                return {};
            }

            const compositions = result.compositionGarrison ?? [];
            const placeName = place || result.dispatchPlace || "";
            const resolvedGasStation = (result.gasStations ?? []).find(station => String(station.name) === String(placeName))?.id ?? "";

            setRows(compositions.map(item => {
                const conductor = item.integrants.find(integrant => integrant.conductor);
                const conductorName = conductor.contingent.namewar;

                return {
                    tempId: nextTempId(),
                    gasStation: resolvedGasStation,
                    vehicleId: item.vehicleId,
                    vehicleName: item.name,
                    conductorId: conductor.contingent.id,
                    conductorName: conductorName,
                    notRefuelling: false,
                    currentKm: isSuggestKm ? item.initialMileage ?? finalMileage ?? "" : "",
                    fullTank: false,
                    authorizationCode: "",
                    file: null,
                    fuels: [{tempId: nextTempId(), fuelType: "", valuePay: "", fuelQuant: ""}],
                }
            }));

            return result;
        },
    });

    const fuelTypeOptions = mapFuelTypeOptions(data?.fuelTypes);
    const gasStationOptions = (data?.gasStations ?? []).map(station => ({value: station.id, label: station.name}));
    const isAuthorizationCode = !!data?.isAuthorizationCodeRefuelling;

    const updateRow = (tempId, partial) =>
        setRows(prev => prev.map(row => row.tempId === tempId ? {...row, ...partial} : row));

    const updateFuel = (rowId, fuelId, partial) =>
        setRows(prev => prev.map(row => row.tempId === rowId
            ? {...row, fuels: row.fuels.map(fuel => fuel.tempId === fuelId ? {...fuel, ...partial} : fuel)}
            : row));

    const addFuel = (rowId) =>
        setRows(prev => prev.map(row => row.tempId === rowId
            ? {...row, fuels: [...row.fuels, {tempId: nextTempId(), fuelType: "", valuePay: "", fuelQuant: ""}]}
            : row));

    const removeFuel = (rowId, fuelId) =>
        setRows(prev => prev.map(row => row.tempId === rowId
            ? {...row, fuels: row.fuels.filter(fuel => fuel.tempId !== fuelId)}
            : row));

    const formatDecimalInput = (value) => {
        const digits = value.replace(/\D/g, "");
        if (!digits) return "";

        const normalized = digits.padStart(3, "0");
        const integerPart = normalized.slice(0, -2).replace(/^0+(?=\d)/, "");
        const decimalPart = normalized.slice(-2);

        return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${decimalPart}`;
    };

    const updateFormattedFuel = (rowId, fuelId, field, value) =>
        updateFuel(rowId, fuelId, {[field]: formatDecimalInput(value)});

    const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

    const validate = () => {
        const missingByVehicle = [];

        rows.forEach(row => {
            if (row.notRefuelling) return;

            const missingFields = new Set();
            if (showLocation && !hasValue(row.gasStation)) missingFields.add("Posto");
            if (!hasValue(row.currentKm)) missingFields.add("Km ao abastecer");

            row.fuels.forEach(fuel => {
                if (!hasValue(fuel.fuelType)) missingFields.add("Tipo combustível");
                if (!hasValue(fuel.valuePay)) missingFields.add("Valor pago");
                if (!hasValue(fuel.fuelQuant)) missingFields.add("Quant. combustível");
            });

            if (missingFields.size > 0) missingByVehicle.push(`${row.vehicleName || "Viatura"}: ${Array.from(missingFields).join(", ")}`);
        });

        if (missingByVehicle.length === 0) return true;

        projectUtils.swalMessage(
            "warning",
            "Campos obrigatórios",
            ["Preencha os campos obrigatórios do abastecimento:", ...missingByVehicle],
            "Ok"
        );

        return false;
    };

    useImperativeHandle(ref, () => ({
        validate,
        collect: () => {
            const list = [];
            const files = [];
            let fileCount = 1;

            rows.forEach(row => {
                if (row.notRefuelling) return;

                let fileName = null;
                if (row.file) {
                    const dot = row.file.name.lastIndexOf(".");
                    fileName = `${row.file.name.substring(0, dot)}-${fileCount}${row.file.name.substring(dot)}`;
                    files.push({field: "refuellingFiles", file: row.file.file, name: fileName});
                    fileCount++;
                }

                row.fuels.forEach(fuel => {
                    list.push({
                        vehicleId: row.vehicleId,
                        conductor: row.conductorId,
                        ...(showLocation ? {gasStation: row.gasStation || null} : {}),
                        fuelType: fuel.fuelType || undefined,
                        authorizationCode: row.authorizationCode || null,
                        valuePay: fuel.valuePay.replace(/\./g, '').replace(',', '.'),
                        fuelQuant: fuel.fuelQuant.replace(/\./g, '').replace(',', '.'),
                        currentKm: row.currentKm,
                        fullTank: row.fullTank,
                        fileName,
                    });
                });
            });

            return {list: list.length > 0 ? list : null, files};
        },
    }), [finalMileage, rows, showLocation]);

    return (
        <>
            {isLoading && <Loader/>}
            <div className={"flex flex-col gap-4"}>
                {rows.map(row => (
                    <div key={row.tempId} className="flex flex-col gap-3 rounded border border-[#ededed] p-3">
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-gray-600">
                                {row.vehicleName} {row.conductorName ? `(${row.conductorName})` : ""}
                            </span>
                            <InputCheckbox
                                id={`notRefuelling-${row.tempId}`}
                                label="Não abastecer"
                                checked={row.notRefuelling}
                                onChange={({target}) => updateRow(row.tempId, {notRefuelling: target.checked})}
                            />
                        </div>

                        {!row.notRefuelling &&
                            <>
                                {showLocation &&
                                    <SentryRow className="items-end">
                                        <SelectComponent
                                            classNameWrapper="col-span-6"
                                            label="Posto *"
                                            options={gasStationOptions}
                                            value={gasStationOptions.find(option => String(option.value) === String(row.gasStation)) ?? null}
                                            onChange={(option) => updateRow(row.tempId, {gasStation: option?.value ?? ""})}
                                        />
                                    </SentryRow>
                                }
                                <SentryRow className="items-end">
                                    <InputForm
                                        classNameWrapper="col-span-3"
                                        id={`currentKm-${row.tempId}`}
                                        label="Km ao abastecer *"
                                        integerOnly
                                        type="number"
                                        value={row.currentKm}
                                        onChange={({target}) => updateRow(row.tempId, {currentKm: target.value})}
                                    />
                                    {isAuthorizationCode &&
                                        <InputForm
                                            classNameWrapper="col-span-3"
                                            id={`authorizationCode-${row.tempId}`}
                                            label="Código de autorização"
                                            value={row.authorizationCode}
                                            onChange={({target}) => updateRow(row.tempId, {authorizationCode: target.value})}
                                        />
                                    }
                                    <div className="col-span-3 flex items-center h-[32px]">
                                        <InputCheckbox
                                            id={`fullTank-${row.tempId}`}
                                            label="Completou o tanque?"
                                            checked={row.fullTank}
                                            onChange={({target}) => updateRow(row.tempId, {fullTank: target.checked})}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <InputFile
                                            file={row.file}
                                            accept="image/*,application/pdf"
                                            setFile={(file) => updateRow(row.tempId, {file: file ?? null})}
                                            removeFile={() => updateRow(row.tempId, {file: null})}
                                        />
                                    </div>
                                </SentryRow>

                                {row.fuels.map((fuel, index) => (
                                    <SentryRow key={index.toString()} className="items-end">
                                        <SelectComponent
                                            id={`fuel-type-${row.tempId}-${index}`}
                                            classNameWrapper="col-span-5"
                                            label="Tipo combustível *"
                                            options={fuelTypeOptions}
                                            value={fuelTypeOptions.find(option => option.value === fuel.fuelType) ?? null}
                                            onChange={(option) => updateFuel(row.tempId, fuel.tempId, {fuelType: option?.value ?? ""})}
                                        />
                                        <InputForm
                                            id={`value-pay-${row.tempId}-${index}`}
                                            classNameWrapper="col-span-3"
                                            label="Valor pago *"
                                            type="text"
                                            value={fuel.valuePay}
                                            onChange={({target}) => updateFormattedFuel(row.tempId, fuel.tempId, "valuePay", target.value)}
                                        />
                                        <InputForm
                                            id={`fuel-quant-${row.tempId}-${index}`}
                                            classNameWrapper="col-span-3"
                                            label="Quant. combustível *"
                                            type="text"
                                            value={fuel.fuelQuant}
                                            onChange={({target}) => updateFormattedFuel(row.tempId, fuel.tempId, "fuelQuant", target.value)}
                                        />
                                        <div className="col-span-1 flex">
                                            {index > 0 &&
                                                <Button type="button" target="danger" title="Remover" onClick={() => removeFuel(row.tempId, fuel.tempId)}><FaTrash/></Button>
                                            }
                                        </div>
                                    </SentryRow>
                                ))}

                                <div className="w-fit">
                                    <Button type="button" target="primary-outline" onClick={() => addFuel(row.tempId)}><FaPlus/> Adicionar combustível</Button>
                                </div>
                            </>
                        }
                    </div>
                ))}
            </div>
        </>
    );
});

DispatchRefuelling.displayName = "DispatchRefuelling";

export default DispatchRefuelling;
